import { skipToken, useMutation, useQuery } from '@tanstack/react-query'

import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type { AxiosRequestConfig } from 'axios'
import type {
  IReview,
  IReportReviewsPayload,
  IReportReviewsResponse,
  IReplyToReviewsPayload,
  IReplyToReviewsResponse,
} from '~/types/review'

import {
  doNotReportReview,
  getReviewAppVersions,
  getReviewHistory,
  getReviewLanguagesList,
  getReviews,
  replyToReviews,
  reportReviews,
  updateReviewTopics,
} from '~/api/reviews'

////////// KEYS //////////

export const keys = {
  all: ['reviews'] as const,
  appVersions: ({ app_key, device_type }: { app_key: string; device_type: string }) =>
    [...keys.all, 'app-versions', app_key, device_type] as const,
  details: (id: string) => [...keys.all, id] as const,
  history: (id: string) => [...keys.all, 'history', id] as const,
  languages: () => [...keys.all, 'languages'] as const,
  list: (params?: AxiosRequestConfig['params']) => [...keys.all, 'listing', params] as const,
}

////////// QUERIES //////////

export const useReviewsQuery = (params?: AxiosRequestConfig['params']): UseQueryResult<IPaginatedResponse<IReview>> => {
  return useQuery({
    queryFn: params?.applications.length ? () => getReviews(params) : skipToken,
    queryKey: keys.list(params),
  })
}

export const useReviewLanguagesListQuery = (): UseQueryResult<string[]> => {
  return useQuery({ queryFn: getReviewLanguagesList, queryKey: keys.languages() })
}

export const useReviewAppVersionsQuery = (
  params: { app_key: string; device_type: string },
  options: { enabled?: boolean }
): UseQueryResult<string[]> => {
  return useQuery({
    ...options,
    queryFn: () => getReviewAppVersions(params),
    queryKey: keys.appVersions(params),
  })
}

export const useReviewHistoryQuery = ({ reviewId }: { reviewId: string }): UseQueryResult<IReview[]> => {
  return useQuery({
    queryFn: () => getReviewHistory(reviewId),
    queryKey: keys.history(reviewId),
  })
}

////////MUTATION //////////

export const useUpdateReviewTopicsMutation = (): UseMutationResult<void, unknown, IReview, unknown> => {
  return useMutation({ mutationFn: updateReviewTopics })
}

export const useReplyToReviewsMutation = (): UseMutationResult<
  IReplyToReviewsResponse,
  unknown,
  IReplyToReviewsPayload,
  unknown
> => {
  return useMutation({ mutationFn: replyToReviews })
}

export const useReportReviewsMutation = (): UseMutationResult<
  IReportReviewsResponse,
  unknown,
  IReportReviewsPayload,
  unknown
> => {
  return useMutation({ mutationFn: reportReviews })
}

export const useDoNotReportReviewMutation = (): UseMutationResult<void, unknown, { reviewId: string }, unknown> => {
  return useMutation({ mutationFn: doNotReportReview })
}
